import { Amplify } from 'aws-amplify';
import { getSsoAwsConfig, getUserPassAwsConfig } from '@/shared/aws';
import { AUTH_MODE } from '@/shared/config';
import { resolveAuthMode as checkAuthMode } from '@/shared/lib/auth/authMode';

// TODO(ts): typed as the AWS Amplify config shape once aws/ is converted
/**
 * Strips the identity-pool entry out of a username-password Amplify configuration. Destructures aws_cognito_identity_pool_id away and keeps the remaining keys, tolerating a nullish configuration.
 *
 * @param config - Raw AWS configuration object.
 * @returns The configuration without its identity-pool id.
 */
const sanitizeUserPassConfig = (config: any) => {
  const { aws_cognito_identity_pool_id: _identityPoolId, ...restConfig } = config || {};
  return restConfig;
};

/**
 * Chooses the Amplify AWS configuration matching the active authentication mode. Loads and sanitizes the username-password config for that mode and the SSO config otherwise, logging which one was picked.
 *
 * @param authMode - Authentication mode to configure for; defaults to username and password.
 * @returns The AWS configuration object for the requested mode.
 */
const getAwsConfigByMode = (authMode = AUTH_MODE.USERNAME_PASSWORD) => {
  if (authMode === AUTH_MODE.USERNAME_PASSWORD) {
    console.log('[Amplify] Loading aws-userpass config');
    return sanitizeUserPassConfig(getUserPassAwsConfig());
  }

  console.log('[Amplify] Loading aws-sso config');
  return getSsoAwsConfig();
};

/**
 * Resolves the AWS configuration for the current or an explicitly given authentication mode. Defaults the argument to the stored mode and delegates the SSO or username-password choice.
 *
 * @param authMode - Authentication mode to configure for; defaults to the stored mode.
 * @returns The AWS configuration object for that mode.
 */
const updatedAwsConfig = (authMode = checkAuthMode()) => getAwsConfigByMode(authMode);

/**
 * Reconfigures Amplify to authenticate through the requested mode. Resolves the matching AWS configuration and hands it to Amplify.configure, defaulting to username and password.
 *
 * @param authMode - Authentication mode to switch to; defaults to username and password.
 * @returns Nothing.
 */
const switchAmplifyAuthMode = (authMode = AUTH_MODE.USERNAME_PASSWORD) => {
  Amplify.configure(updatedAwsConfig(authMode));
};

export { checkAuthMode, switchAmplifyAuthMode, getAwsConfigByMode };

export default updatedAwsConfig;
